import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import * as DashboardActions from '@app/Store/actions/dashboard-actions';
import { ActionDialogComponent } from '../action-dialogs/action-dialog/action-dialog.component';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/Store';
import { AddParentChildIn } from '@app/Shared/models/add-parent-in.model';
import { RemoveParentChildIn } from '@app/Shared/models/remove-parent-in.model';
import { ChangeParentChildLinkKey } from '@app/Shared/models/change-parent-child-link-key.model';
import { ReassignMatch } from '@app/Shared/models/reassign-match.model';
import { OverrideHistoryIn } from '@app/Shared/models/override-history-in.model';
import { ReturnSurvivorshipIn } from '@app/Shared/models/return-survivorship-in.model';

@Component({
    selector: 'app-action-row',
    templateUrl: './action-row.component.html',
    styleUrls: ['./action-row.component.scss'],
})
export class ActionRowComponent implements OnInit {
    @Input() selectedTab: string = 'Parent';
    @Input() activeChange: boolean = true;
    @Input() activeRemove: boolean = true;
    @Input() activeAdd: boolean = true;
    @Input() data: ActionRowData;
    

    constructor(private dialog: MatDialog, private alertService: AlertService, private store: Store<AppState>, private cdr: ChangeDetectorRef) {}
    ngOnInit(): void {}

    openDialog(action: string): void {
        if (this.selectedTab == 'Parent') {
            if (action.toLowerCase() == 'add') this.handleAddParent();
            if (action.toLowerCase() == 'remove') this.handleRemoveParent();
        }
        if (this.selectedTab == 'Child') {
            if (action.toLowerCase() == 'add') this.handleAddChild();
            if (action.toLowerCase() == 'remove') this.handleRemoveChild();
        }
        if (action.toLowerCase() == 'change') this.handleChange();
        if (action.toLowerCase() == 'reassign') this.handleReassign();

        if (action.toLowerCase() == 'override history') this.handleOverrideHistory();

        if (action.toLowerCase() == 'return survivorship') this.handleReturnSurvivorship();
    }

    private handleReturnSurvivorship(){
        if (this.selectedTab == 'Merge' && (this.data.mergeInfo == null || this.data.masterKey == null)) {
            this.alertService.notify(AlertType.Warning, `Please select a ${this.selectedTab} to return survivorship`, '', 5000);
            return;
        }
        this.alertService.confirm('Are you sure you want to return the survivorship for this record?', 'Return Survivorship', () => {
            const sendModel: ReturnSurvivorshipIn = {
                masterTable: this.data.masterTable,
                masterKey: this.data.masterKey,
                historyKey: this.data.mergeInfo.historyKey,
            };
            this.store.dispatch(DashboardActions.returnSurvivorship(sendModel));
        });
    }

    private handleRemoveParent() {
        if (this.data.masterKey == null || this.data.parentInfo == null || this.data.parentInfo.linkKey == null) {
            this.alertService.notify(AlertType.Warning, 'Please select a Parent to be removed', '', 5000);
            return;
        }

        this.alertService.confirm('Are you sure you want to remove the selected parent record, and orphan this master?', 'Remove Parent', () => {
            const sendModel: RemoveParentChildIn = {
                masterTable: this.data.masterTable,
                masterKey: this.data.masterKey,
                linkTable: this.data.linkTable,
                linkKey: this.data.parentInfo.linkKey,
                relationship: this.selectedTab,
            };
            this.store.dispatch(DashboardActions.removeParentChildData(sendModel));
        });
    }

    private handleRemoveChild() {
        if (this.data.masterKey == null || this.data.childInfo == null) {
            this.alertService.notify(AlertType.Warning, 'Please select a Child to be removed', '', 5000);
            return;
        }

        this.alertService.confirm('Are you sure you want to remove the selected child record, and orphan this master?', 'Remove Child', () => {
            const sendModel: RemoveParentChildIn = {
                masterTable: this.data.masterTable,
                masterKey: this.data.masterKey,
                linkTable: this.data.linkTable,
                linkKey: this.data.childInfo.linkKey,
                relationship: this.selectedTab,
            };
            this.store.dispatch(DashboardActions.removeParentChildData(sendModel));
        });
    }

    private handleChange() {
        if (this.selectedTab == 'Parent' && (this.data.parentInfo == null || this.data.masterKey == null)) {
            this.alertService.notify(AlertType.Warning, `Please select a ${this.selectedTab} to be Changed`, '', 5000);
            return;
        }
        if (this.selectedTab == 'Child' && (this.data.childInfo == null || this.data.masterKey == null)) {
            this.alertService.notify(AlertType.Warning, `Please select a ${this.selectedTab} to be Changed`, '', 5000);
            return;
        }

        const dialogRef = this.dialog.open(ActionDialogComponent, {
            width: '250px',
            data: { dialogType: 'Change', relationship: this.selectedTab, title:`Enter New ${this.selectedTab} Master Key:` },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.status === true) {
                const sendModel: ChangeParentChildLinkKey = {
                    masterTable: this.data.masterTable,
                    masterKey: this.data.masterKey,
                    linkTable: this.data.linkTable,
                    linkKey: this.data.linkKey,
                    newMasterKey: result.value,
                    relationship: this.selectedTab,
                };

                this.store.dispatch(DashboardActions.changeParentChildData(sendModel));
            }
        });
    }

    private handleAddParent() {
        const dialogRef = this.dialog.open(ActionDialogComponent, {
            width: '250px',
            data: { dialogType: 'Add', relationship: 'Parent', title: 'Enter New Parent Master Key:' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.status === true) {
                const sendModel: AddParentChildIn = {
                    masterTable: this.data.masterTable,
                    masterKey: this.data.masterKey,
                    linkTable: this.data.linkTable,
                    relationship: 'Parent',
                    newRelationKey: result.value,
                };

                this.store.dispatch(DashboardActions.addParentChildData(sendModel));
            }
        });
    }

    private handleAddChild() {
        const dialogRef = this.dialog.open(ActionDialogComponent, {
            width: '250px',
            data: { dialogType: 'Add', relationship: 'Child', title:'Enter New Child Master Key:' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.status === true) {
                const sendModel: AddParentChildIn = {
                    masterTable: this.data.masterTable,
                    masterKey: this.data.masterKey,
                    linkTable: this.data.linkTable,
                    relationship: 'Child',
                    newRelationKey: result.value,
                };

                this.store.dispatch(DashboardActions.addParentChildData(sendModel));
            }
        });
    }

    private handleReassign() {
        if (this.selectedTab == 'Match' && (this.data.matchInfo == null || this.data.masterKey == null)) {
            this.alertService.notify(AlertType.Warning, `Please select a ${this.selectedTab} to be Reassigned`, '', 5000);
            return;
        }
        

        const dialogRef = this.dialog.open(ActionDialogComponent, {
            width: '250px',
            data: { dialogType: 'Reassign', relationship: this.selectedTab, title:'Enter Master Key for Source Reassignment:' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.status === true) {
                const sendModel: ReassignMatch = {
                    masterTable: this.data.masterTable,
                    masterKey: this.data.masterKey,
                    newMasterKey: result.value,
                    datasetName: this.data.matchInfo.dataSetName,
                    matchKey: this.data.matchInfo.matchKey
                };
                this.store.dispatch(DashboardActions.reassignMatch(sendModel));
            }
        });
    }


    private handleOverrideHistory() {
        if (this.selectedTab == 'Merge' && (this.data.mergeInfo == null || this.data.masterKey == null)) {
            this.alertService.notify(AlertType.Warning, `Please select a ${this.selectedTab} to Override`, '', 5000);
            return;
        }
        

        const dialogRef = this.dialog.open(ActionDialogComponent, {
            width: '250px',
            data: { dialogType: 'Override History', type: 'text',placeholder: 'New Value' ,relationship: this.selectedTab, title:`Enter new value to override ${this.data.mergeInfo.currentValue} to:` },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result.status === true) {
                const sendModel: OverrideHistoryIn = {
                    masterTable: this.data.masterTable,
                    masterKey: this.data.masterKey,
                    historyKey: this.data.mergeInfo.historyKey,
                    newFieldValue: result.value
                };
                this.store.dispatch(DashboardActions.overrideHistory(sendModel));
            }
        });
    }


}

export class ActionRowData {
    masterTable: string;
    masterKey: number;
    [index: string]: any;
}
